import { inject, Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, firstValueFrom } from 'rxjs';
import { UserRole } from '@app/core/auth/user-role.enum';
import { environment } from '@env';

export interface ApplicationRef {
  id: string;
  name: string;
}

export interface ProfileAssignment {
  id: string;
  code: string;
  application: ApplicationRef;
}

export interface SubsidiaryAssignment {
  id: string;
  name: string;
  codeOkapi: string;
  profiles: ProfileAssignment[];
}

export interface UserProfile {
  oid: string;
  id: string;
  upn: string;
  firstName: string;
  lastName: string;
  subsidiaries: SubsidiaryAssignment[];
}

/** Réponse de `POST /auth/login` : le back échange la session Entra active
 * contre un profil SmartPlanner (résolu auprès de BO Users côté back) et un
 * JWT applicatif (`profileToken`) qui encapsule ce profil. */
interface LoginResponse {
  profileToken: string;
  user: UserProfile;
}

/** Rôles SmartPlanner reconnus : toute valeur renvoyée dans le `profileToken`
 * qui ne figure pas dans cet ensemble est ignorée (mesure de sécurité, l'API
 * externe pouvant exposer d'autres rôles applicatifs non liés à SmartPlanner). */
const SMARTPLANNER_ROLES = new Set<string>(Object.values(UserRole));

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly authService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly http = inject(HttpClient);
  private readonly _profile = signal<UserProfile | null>(null);
  /** JWT applicatif renvoyé par `POST /auth/login`, à joindre par l'intercepteur
   * (`x-profile-token`) sur les appels au back pour qu'il vérifie le profil de
   * l'utilisateur. Sa durée de vie suit la session Entra : un changement de
   * profil/filiale côté BO Users nécessite une déconnexion/reconnexion Entra
   * pour être répercuté (pas de rafraîchissement silencieux du JWT). */
  private readonly _profileToken = signal<string | null>(null);
  /** Mémorise la requête de profil en vol pour que les appelants concurrents
   * (guards, broadcast MSAL, redirect handler) partagent le même appel HTTP
   * au lieu d'en déclencher un nouveau à chaque invocation de `getUserRoles`. */
  private loadingProfile: Promise<void> | null = null;

  readonly profile = this._profile.asReadonly();
  readonly profileToken = this._profileToken.asReadonly();
  readonly activeSubsidiary = computed(() => this._profile()?.subsidiaries[0] ?? null);

  /**
   * Rôles de la filiale active de l'utilisateur, filtrés aux seuls rôles
   * SmartPlanner connus. Le `profileToken` renvoie une liste de filiales, mais
   * SmartPlanner ne gère pour l'instant qu'une seule filiale par utilisateur :
   * on ne retient donc que la première filiale de la liste (un choix explicite
   * de filiale post-auth pourra être ajouté plus tard si besoin).
   */
  readonly roles = computed<UserRole[]>(() => {
    const codes = this.activeSubsidiary()?.profiles.map((profile) => profile.code) ?? [];
    return [...new Set(codes.filter((code): code is UserRole => SMARTPLANNER_ROLES.has(code)))];
  });
  readonly isPlanificateur = computed(() => this.roles().includes(UserRole.Planificateur));
  readonly isConducteur = computed(() => this.roles().includes(UserRole.Conducteur));

  isAuthenticated(): boolean {
    return !!this.authService.instance.getActiveAccount();
  }

  /**
   * Point d'entrée unique de l'init post-auth (US #5/#6) : attend la fin du
   * traitement MSAL (`inProgress$` → `None`), s'assure qu'un compte actif est
   * défini, puis résout le profil applicatif si besoin. Idempotent : un
   * profil déjà chargé n'est jamais re-fetché.
   */
  async getUserRoles(): Promise<UserRole[]> {
    await this.waitForMsal();

    if (!this.isAuthenticated()) {
      return [];
    }

    if (!this._profile()) {
      this.loadingProfile ??= this.loadProfile().finally(() => {
        this.loadingProfile = null;
      });
      await this.loadingProfile;
    }

    return this.roles();
  }

  clearSession(): void {
    this._profile.set(null);
    this._profileToken.set(null);
  }

  private async waitForMsal(): Promise<void> {
    await firstValueFrom(
      this.msalBroadcastService.inProgress$.pipe(
        filter((status) => status === InteractionStatus.None),
      ),
    );

    if (!this.authService.instance.getActiveAccount()) {
      const [firstAccount] = this.authService.instance.getAllAccounts();
      if (firstAccount) {
        this.authService.instance.setActiveAccount(firstAccount);
      }
    }
  }

  /**
   * Le profil (filiales + rôles) n'est jamais décodé depuis le token côté front :
   * `POST /auth/login` authentifie l'appel via le Bearer Entra déjà attaché par
   * `msalInterceptorFn`, résout le profil auprès de BO Users côté back, puis
   * renvoie un JWT applicatif (`profileToken`) encapsulant ce profil.
   */
  private async loadProfile(): Promise<void> {
    const account = this.authService.instance.getActiveAccount();
    if (!account) {
      this.clearSession();
      return;
    }

    try {
      const { profileToken, user } = await firstValueFrom(
        this.http.post<LoginResponse>(`${environment.apiConfig.uri}/auth/login`, {}),
      );
      this._profileToken.set(profileToken);
      this._profile.set(user);
    } catch {
      this.clearSession();
    }
  }
}
